import { useState } from 'react';
import type { ReactElement } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useWorkspaces } from '../api/queries';
import { useRemix } from '../api/public-queries';
import { ErrorBanner } from '../components/ErrorBanner';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { SelectField } from '../ui/Select';

/**
 * Copies a published report's layout into one of the viewer's workspaces. The
 * data is never copied. Cancelling does nothing; only the confirm button posts.
 */
export function RemixDialog(props: {
  linkId: string;
  activeWorkspaceId: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const router = useRouter();
  const workspaces = useWorkspaces();
  const remix = useRemix();
  const [picked, setPicked] = useState<string | null>(null);

  const list = workspaces.data ?? [];
  const has = (id: string | null): id is string => id !== null && list.some((w) => w.id === id);
  // The person's pick, else the active workspace, else the first one.
  const selected = has(picked) ? picked : has(props.activeWorkspaceId) ? props.activeWorkspaceId : (list[0]?.id ?? '');
  const error = remix.error ?? workspaces.error;

  function confirm(): void {
    if (!selected || remix.isPending) return;
    remix.mutate(
      { linkId: props.linkId, targetWorkspaceId: selected },
      {
        onSuccess: (result) => {
          void router.navigate({
            to: '/w/$workspaceId/reports/$reportId',
            params: { workspaceId: selected, reportId: result.reportId }
          });
        }
      }
    );
  }

  return (
    <Dialog
      title="Remix this report"
      size="sm"
      isOpen={props.isOpen}
      onOpenChange={props.onOpenChange}
      footer={
        <>
          <Button variant="quiet" isDisabled={remix.isPending} onPress={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" busy={remix.isPending} isDisabled={!selected} onPress={confirm}>
            Remix
          </Button>
        </>
      }
    >
      <p className="m-0 p-0 text-md text-muted">Copy this report&apos;s layout into your workspace. The data isn&apos;t copied.</p>
      <SelectField
        label="Workspace"
        options={list.map((w) => ({ key: w.id, label: w.name }))}
        value={selected}
        onChange={setPicked}
      />
      <ErrorBanner error={error} />
    </Dialog>
  );
}
